// cart-drawer.js

// A cart line's identity that survives Shopify re-keying it. Shopify gives a
// line a NEW key whenever the set of discounts on it changes (a hamper code
// arriving or leaving does exactly that, and so does a quantity that drops a
// code below its minimum), so a key read when the shopper tapped can be gone by
// the time the request leaves. Variant + properties is how Shopify itself
// decides two adds are the same line, so it is stable. The markup carries both
// (data-variant / data-props in snippets/cart-line-items.liquid).
function lineSig(variantId, props) {
  const p = props || {};
  return String(variantId) + '|' + JSON.stringify(Object.keys(p)
    .filter((k) => String(p[k]).trim() !== '')
    .sort()
    .map((k) => [k, String(p[k])]));
}
function liSig(li) {
  if (!li) return '';
  if (!li.dataset.variant) return 'key:' + (li.dataset.key || '');
  let props = {};
  try { props = JSON.parse(li.dataset.props || '{}') || {}; } catch (e) { props = {}; }
  return lineSig(li.dataset.variant, props);
}
function findLine(scope, sig) {
  if (!scope || !sig) return null;
  return Array.from(scope.querySelectorAll('.cart-item')).find((li) => liSig(li) === sig) || null;
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.cart-drawer__dialog');
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.refresh());
    this.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
    this.addEventListener('click', (e) => this.onQty(e));
    // Typing a number into the quantity box did nothing: only the -/+ clicks
    // were handled, so the box showed 3 while the cart kept 1.
    this.addEventListener('change', (e) => this.onQtyTyped(e));
    this.addEventListener('submit', (e) => this.onDiscount(e));
    this.addEventListener('click', (e) => this.onCouponClick(e));
    this.addEventListener('click', (e) => this.onRecAdd(e));
    this.addEventListener('input', (e) => { if (e.target.name === 'coupon') { this._cpn = this._cpn || {}; this._cpn.typed = e.target.value; } });
    // Escape with the coupon or colour sheet open closes the sheet only. The <dialog>
    // turns Escape into a "cancel" that would shut the whole drawer.
    const sheetEsc = (e) => {
      if (!this._pick && !(this._cpn && this._cpn.sheet)) return;
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation();
      if (this._pick) this.closePick(); else this.toggleSheet(false);
    };
    this.addEventListener('keydown', sheetEsc, true);
    this.dialog?.addEventListener('cancel', sheetEsc);
    // Quantity the shopper has asked for, per line key, while +/- requests are
    // still on the wire. Each tap used to read the box, which only changes when
    // the server answers, so three quick taps from 1 sent quantity 2 three
    // times and the cart ended on 2 (the /cart page had the same bug, fixed
    // the same way further down). Requests also go out one at a time, in order.
    this._want = {};
    this._inflight = 0;
    this._chain = Promise.resolve();
    this.initRecDrag();
  }
  // Drag-to-scroll for the "You may also like" rail. Delegated on the persistent
  // cart-drawer element so it keeps working after the body is re-rendered.
  initRecDrag() {
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;
      const track = e.target.closest('[data-cart-recs]');
      if (!track) return;
      this._rec = { track, x: e.clientX, left: track.scrollLeft };
      this._recMoved = false;
      track.classList.add('cursor-grabbing');
    });
    window.addEventListener('pointermove', (e) => {
      if (!this._rec) return;
      const dx = e.clientX - this._rec.x;
      if (Math.abs(dx) > 3) this._recMoved = true;
      this._rec.track.scrollLeft = this._rec.left - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!this._rec) return;
      this._rec.track.classList.remove('cursor-grabbing');
      this._rec = null;
    });
    this.addEventListener('click', (e) => {
      if (this._recMoved && e.target.closest('[data-cart-recs]')) { e.preventDefault(); e.stopPropagation(); this._recMoved = false; }
    }, true);
  }
  // ---- "You may also like" Add to cart (sections/cart-drawer.liquid) -----
  // A product with nothing to choose goes straight in; one with a colour to
  // choose opens the [data-rec-pick] sheet first. The drawer repaints with the
  // product in it (and out of the rail), and the gift engine re-checks gifts
  // and the hamper offer on cart:changed. A refused add (sold out, a quantity
  // limit, a rate limit) says so on the button, then puts the label back.
  async addVariant(id, btn) {
    if (!id || btn.getAttribute('aria-busy') === 'true') return false;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const label = btn.textContent;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Adding…';
    let data = null;
    try {
      const r = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: 'cart-drawer,header' }) });
      try { data = await r.json(); } catch (err) { data = null; }
      if (!r.ok || !data || data.status) data = null;
    } catch (err) { data = null; }
    btn.removeAttribute('aria-busy');
    if (data) {
      this.closePick();
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:changed'));
      return true;
    }
    btn.textContent = 'Try again';
    setTimeout(() => { if (btn.isConnected && btn.textContent === 'Try again') btn.textContent = label; }, 2600);
    return false;
  }
  onRecAdd(e) {
    const t = e.target;
    const direct = t.closest('[data-rec-add]');
    if (direct) { e.preventDefault(); return this.addVariant(direct.dataset.recAdd, direct); }
    const opener = t.closest('[data-rec-pick-open]');
    if (opener) { e.preventDefault(); return this.openPick(opener); }
    if (!t.closest('[data-rec-pick]')) return;
    if (t.closest('[data-rec-pick-close]')) { e.preventDefault(); return this.closePick(); }
    const opt = t.closest('[data-rec-opt]');
    if (opt && !opt.disabled) { e.preventDefault(); return this.choosePick(opt); }
    const add = t.closest('[data-rec-pick-add]');
    if (add && !add.disabled && this._pick && this._pick.chosen) { e.preventDefault(); return this.addVariant(this._pick.chosen.id, add); }
  }
  openPick(opener) {
    const sheet = this.querySelector('[data-rec-pick]');
    let vars = [];
    try { vars = JSON.parse(opener.closest('[data-rec-card]')?.querySelector('[data-rec-variants]')?.textContent || '[]'); } catch (err) { vars = []; }
    if (!sheet || !vars.length) return;
    const opt = (opener.dataset.option || 'option').trim();
    const what = /colou?r/i.test(opt) ? 'colour' : opt.toLowerCase();
    this._pick = { vars, what, chosen: null, opener };
    const img = sheet.querySelector('[data-rec-pick-img]');
    if (img) { if (opener.dataset.img) img.src = opener.dataset.img; else img.removeAttribute('src'); }
    sheet.querySelector('[data-rec-pick-title]').textContent = opener.dataset.title || '';
    const prices = [...new Set(vars.map((v) => v.price))];
    sheet.querySelector('[data-rec-pick-price]').textContent = prices.length === 1 ? prices[0] : 'From ' + prices[0];
    const box = sheet.querySelector('[data-rec-pick-opts]');
    box.setAttribute('aria-label', 'Choose a ' + what);
    box.replaceChildren(...vars.map((v, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cdp__opt';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', 'false');
      b.dataset.recOpt = String(i);
      b.disabled = !v.ok;
      const sw = document.createElement('span');
      sw.className = 'cdp__sw';
      sw.style.background = v.hex || '#cbb89c';
      if (v.img) { const im = document.createElement('img'); im.src = v.img; im.alt = ''; im.width = 80; im.height = 80; sw.appendChild(im); }
      const name = document.createElement('span');
      name.textContent = v.name;
      b.append(sw, name);
      b.setAttribute('aria-label', v.name + (v.ok ? '' : ', sold out'));
      return b;
    }));
    this.paintPick();
    sheet.hidden = false;
    (box.querySelector('.cdp__opt:not(:disabled)') || sheet.querySelector('[data-rec-pick-close]'))?.focus({ preventScroll: true });
  }
  choosePick(opt) {
    if (!this._pick) return;
    this._pick.chosen = this._pick.vars[Number(opt.dataset.recOpt)] || null;
    this.querySelectorAll('[data-rec-pick] [data-rec-opt]').forEach((b) => b.setAttribute('aria-checked', b === opt ? 'true' : 'false'));
    const img = this.querySelector('[data-rec-pick-img]');
    if (img && this._pick.chosen && this._pick.chosen.img) img.src = this._pick.chosen.img;
    this.paintPick();
  }
  paintPick() {
    const sheet = this.querySelector('[data-rec-pick]');
    const p = this._pick;
    if (!sheet || !p) return;
    const label = sheet.querySelector('[data-rec-pick-label]');
    label.textContent = 'Choose a ' + p.what;
    if (p.chosen) { label.append(': '); const b = document.createElement('b'); b.textContent = p.chosen.name; label.appendChild(b); }
    if (p.chosen) sheet.querySelector('[data-rec-pick-price]').textContent = p.chosen.price;
    const add = sheet.querySelector('[data-rec-pick-add]');
    add.disabled = !p.chosen;
    add.textContent = p.chosen ? 'Add to cart' : 'Choose a ' + p.what;
  }
  closePick() {
    const sheet = this.querySelector('[data-rec-pick]');
    if (!sheet || sheet.hidden) return;
    sheet.hidden = true;
    const back = this._pick && this._pick.opener;
    this._pick = null;
    if (back && back.isConnected) back.focus({ preventScroll: true });
  }
  open() {
    if (!this.dialog) return;
    this.dialog.showModal();
    // showModal() focuses the first button, the close X, and iOS rings it in
    // blue. Same task, so the ring is never painted.
    this.querySelector('[data-cart-title]')?.focus({ preventScroll: true });
    document.body.style.overflow = 'hidden';
    // next frame: add is-open so the CSS transform transition plays (slide in)
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
  }
  close() {
    if (!this.dialog) return;
    if (this._cpn) { this._cpn.sheet = false; this._cpn.msg = null; }
    this.closePick();
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    const d = this.dialog;
    let done = false;
    const finish = () => { if (done) return; done = true; d.close(); };
    d.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 500); // fallback (reduced motion / no transition)
  }
  async onQty(e) {
    const bouquetRemove = e.target.closest('[data-bouquet-remove]');
    if (bouquetRemove) { e.preventDefault(); return this.removeBouquet(bouquetRemove.getAttribute('data-bouquet-remove')); }
    const remove = e.target.closest('[data-cart-remove]');
    const btn = e.target.closest('[data-qty-up],[data-qty-down]');
    if (!remove && !btn) return;
    const li = (remove || btn).closest('.cart-item');
    if (!li) return;
    let q;
    if (remove) {
      q = 0;
    } else {
      const sig = liSig(li);
      const input = li.querySelector('[data-qty-input]');
      const base = Object.prototype.hasOwnProperty.call(this._want, sig) ? this._want[sig] : parseInt(input?.value, 10);
      if (Number.isNaN(base)) return;
      q = Math.max(0, base + (btn.hasAttribute('data-qty-up') ? 1 : -1));
    }
    return this.queueQty(li, q);
  }
  // One request at a time, in tap order, each aimed at the line (not the key
  // it had when tapped). See lineSig() for why.
  queueQty(li, q) {
    const sig = liSig(li);
    const key0 = li.dataset.key;
    const input = li.querySelector('[data-qty-input]');
    this._want[sig] = q;
    if (input) input.value = q; // show the tap at once; the server's answer repaints it
    this._inflight++;
    this._chain = this._chain
      .then(() => this.sendQty(sig, key0, q))
      .catch(() => {})
      .then(() => {
        this._inflight--;
        if (!this._inflight) this._want = {};
      });
    return this._chain;
  }
  async postQty(key, q) {
    try {
      const r = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: q, sections: 'cart-drawer,header' }) });
      let data = null;
      try { data = await r.json(); } catch (e) { data = null; }
      return { ok: r.ok && !!data && !data.status, status: r.status, data };
    } catch (err) { return { ok: false, status: 0, data: null }; }
  }
  async sendQty(sig, key0, q) {
    const keyNow = () => { const el = findLine(this, sig); return el ? el.dataset.key : ''; };
    let key = keyNow() || key0;
    let res = await this.postQty(key, q);
    // The line was re-keyed under us (a discount came or went). Let the cart
    // settle, repaint, find the line again and try once more.
    if (!res.ok && (res.status === 400 || res.status === 404)) {
      if (window.taabaaGiftsReady) { try { await window.taabaaGiftsReady(4000); } catch (e) {} }
      await this.refresh();
      const again = keyNow();
      if (again && again !== key) { key = again; res = await this.postQty(key, q); }
    }
    // Refused (over stock, rate limited, line gone): forget the optimistic
    // number and put the drawer back to what the cart really holds.
    if (!res.ok) { delete this._want[sig]; this.refresh(); return; }
    this.renderSections(res.data.sections);
    // Announce it. This is the path that lowers the cart total, and for a long
    // time it was the only cart write that told nobody: the free-gift ladder
    // never learned the cart had dropped, so a gift stayed free below its
    // threshold. The ladder also watches the network as a backstop, but saying
    // it out loud is what makes the behaviour deterministic.
    document.dispatchEvent(new CustomEvent('cart:changed'));
  }
  async onQtyTyped(e) {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const li = input.closest('.cart-item');
    if (!li) return;
    const q = Math.max(0, parseInt(input.value, 10));
    if (Number.isNaN(q)) { this.refresh(); return; }
    // Same queue as the +/- buttons; a refusal puts the box back to the cart.
    return this.queueQty(li, q);
  }
  // Remove every line that belongs to one bouquet in a single update.
  async removeBouquet(bid) {
    if (!bid) return;
    const keys = Array.from(this.querySelectorAll('.cart-item'))
      .filter((li) => li.dataset.bouquet === bid)
      .map((li) => li.dataset.key);
    if (!keys.length) return;
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    const r = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates, sections: 'cart-drawer,header' }) });
    if (!r.ok) return;
    const data = await r.json();
    this.renderSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:changed'));
  }
  // ---- coupons (snippets/cart-coupons.liquid) ---------------------------
  // The box is repainted with the drawer on every cart change, so what the
  // shopper has open and the last message live here and are put back after
  // each paint (paintCoupons, called from renderSections).
  toggleSheet(open) {
    this._cpn = this._cpn || {};
    this._cpn.sheet = !!open;
    this._cpnAnim = !!open; // slide up when opened by the shopper, not on a repaint
    if (!open) this._cpn.msg = null;
    this.paintCoupons();
  }
  onCouponClick(e) {
    const t = e.target;
    if (t.closest('[data-coupon-sheet-open]')) { e.preventDefault(); return this.toggleSheet(true); }
    if (t.closest('[data-coupon-sheet-close]')) { e.preventDefault(); return this.toggleSheet(false); }
    const tog = t.closest('[data-coupon-toggle]');
    if (tog) {
      const k = tog.getAttribute('data-coupon-toggle');
      this._cpn = this._cpn || {};
      this._cpn[k] = !this._cpn[k];
      this.paintCoupons();
      if (k === 'form' && this._cpn.form) this.querySelector('[data-coupon-form] input')?.focus();
      return;
    }
    const ap = t.closest('[data-coupon-apply]');
    if (ap) { e.preventDefault(); return this.applyCoupon(ap.getAttribute('data-coupon-apply'), ap); }
    const rm = t.closest('[data-coupon-remove]');
    if (rm) { e.preventDefault(); return this.removeCoupon(rm.getAttribute('data-coupon-remove'), rm); }
  }
  async onDiscount(e) {
    const form = e.target.closest('[data-coupon-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('[name=coupon]');
    const code = (input?.value || '').trim();
    if (!code) { this.couponMsg('Enter a code first.', false); return; }
    await this.applyCoupon(code, form.querySelector('button[type=submit]'));
  }
  couponMsg(text, ok) {
    this._cpn = this._cpn || {};
    this._cpn.msg = text ? { text, ok } : null;
    // A result clears itself after a few seconds: the cart keeps changing
    // under it, and a stale "X removed." or "Y saves more" is only noise.
    // "Applying…" stays until its own result replaces it.
    clearTimeout(this._cpnT);
    if (text && !/…$/.test(text)) this._cpnT = setTimeout(() => { this._cpn.msg = null; this.paintCoupons(); }, ok ? 4000 : 8000);
    this.paintCoupons();
  }
  paintCoupons() {
    const box = this.querySelector('[data-coupons-box]');
    if (!box) return;
    const st = this._cpn || {};
    const sheet = box.querySelector('[data-coupon-sheet]');
    if (sheet) {
      sheet.hidden = !st.sheet;
      sheet.classList.toggle('no-anim', !this._cpnAnim);
      this._cpnAnim = false;
      const inp = sheet.querySelector('[name=coupon]');
      if (inp && st.typed != null && inp.value !== st.typed) inp.value = st.typed;
    }
    const form = box.querySelector('[data-coupon-form]');
    const list = box.querySelector('[data-coupon-list]');
    if (form) form.hidden = !st.form;
    if (list) list.hidden = !st.list;
    box.querySelectorAll('[data-coupon-toggle]').forEach((b) => b.setAttribute('aria-expanded', st[b.getAttribute('data-coupon-toggle')] ? 'true' : 'false'));
    const msg = box.querySelector('[data-coupon-msg]');
    if (msg) {
      msg.textContent = st.msg ? st.msg.text : '';
      msg.classList.toggle('is-ok', !!(st.msg && st.msg.ok));
      msg.classList.toggle('is-bad', !!(st.msg && !st.msg.ok));
    }
  }
  // The Fastrr checkout reads ONE code, from the discount_code cookie, so it
  // must always name the code that is actually saving money. Same cookie shape
  // Shopify's /discount/<code> link writes (host-only, path /, Lax).
  setCodeCookie(code) {
    try {
      document.cookie = code ? 'discount_code=' + encodeURIComponent(code) + '; path=/; SameSite=Lax'
                             : 'discount_code=; path=/; max-age=0; SameSite=Lax';
    } catch (e) {}
  }
  async applyCoupon(raw, btn) {
    const code = String(raw || '').trim().toUpperCase();
    if (!code) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const money = (p) => '₹ ' + (p / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    // An order-offer code typed outside its tier (TAABAA10 on a Rs 2,000+
    // order, FESTIVE500 on Rs 2,500) is explained instead of applied: the
    // cart already carries the tier's own code, and TAABAA10 must never sit
    // on an order of Rs 2,000 or more (that is its Rs 200 cap).
    const tierMsg = await this.offerTierCheck(code, root, money);
    if (tierMsg) { this.couponMsg(tierMsg, false); return; }
    btn?.setAttribute('aria-busy', 'true');
    this.couponMsg('Applying ' + code + '…', true);
    try {
      // /discount/<code> puts it on the cart AND in the cookie Fastrr reads;
      // the redirect hands back the cart so this is one round trip.
      const r = await fetch(root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'cart.js'), { headers: { Accept: 'application/json' } });
      let cart = null;
      try { cart = await r.json(); } catch (e) { cart = null; }
      if (!cart || !Array.isArray(cart.discount_codes)) cart = await (await fetch(root + 'cart.js')).json();
      const codes = cart.discount_codes || [];
      const mine = codes.find((d) => String(d.code).toUpperCase() === code);
      const on = codes.filter((d) => d.applicable).map((d) => String(d.code));
      let text, ok;
      if (mine && mine.applicable) {
        ok = true;
        text = code + ' applied.' + (cart.total_discount > 0 ? ' You save ' + money(cart.total_discount) + '.' : '');
        this.setCodeCookie(mine.code);
      } else {
        ok = false;
        const other = on.find((c) => c.toUpperCase() !== code);
        // Shopify answers a wrong code and a weaker one the same way (on the
        // cart, applicable:false), so the message has to cover both honestly.
        text = other ? code + " not applied. It isn't valid here, or " + other + ' saves more (one code per order).'
                     : code + " isn't valid on this cart.";
        // A code that saves nothing comes off again, so the cart and the
        // checkout are never left holding it, and the cookie goes back to the
        // code that does (or none).
        const keep = codes.map((d) => String(d.code)).filter((c) => c.toUpperCase() !== code);
        await fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: keep.join(',') }) }).catch(() => {});
        this.setCodeCookie(other || '');
      }
      if (ok) { const inp = this.querySelector('[data-coupon-form] input'); if (inp) inp.value = ''; this._cpn.form = false; this._cpn.typed = ''; this._cpn.sheet = false; }
      this.couponMsg(text, ok);
      await this.refresh();
      // The gift engine re-checks the hamper offer against the new code (and
      // points the cookie at whichever code wins).
      document.dispatchEvent(new CustomEvent('cart:changed'));
    } catch (err) {
      this.couponMsg("Couldn't apply that code. Please try again.", false);
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  }
  // The order-offer tiers (snippets/hamper-offer.liquid cfg). Returns a
  // message when `code` is a tier code the cart is not in, else ''.
  async offerTierCheck(code, root, money) {
    let cfg = null;
    try { cfg = JSON.parse(document.querySelector('[data-hamper-offer-cfg]')?.textContent || 'null'); } catch (e) { cfg = null; }
    const tiers = (cfg && cfg.tiers || []).slice().sort((a, b) => (Number(a.m) || 0) - (Number(b.m) || 0));
    const i = tiers.findIndex((t) => String(t.code).toUpperCase() === code);
    if (i < 0) return '';
    let cart = null;
    try { cart = await (await fetch(root + 'cart.js')).json(); } catch (e) { return ''; }
    const v = (cart.items || []).reduce((sum, it) => {
      const p = it.properties || {};
      return p._free_gift || p._was_free_gift ? sum : sum + (Number(it.original_line_price) || 0);
    }, 0);
    const from = (Number(tiers[i].m) || 0) * 100;
    const to = tiers[i + 1] ? (Number(tiers[i + 1].m) || 0) * 100 : Infinity;
    const name = (t) => (Number(t.pct) > 0 ? t.pct + '% off' : money((Number(t.r) || 0) * 100) + ' off');
    if (v >= to) {
      const own = tiers.filter((t) => v >= (Number(t.m) || 0) * 100).pop();
      return code + ' is for orders under ' + money(to) + '. ' + own.code + ' (' + name(own) + ') is on your order instead.';
    }
    if (v < from) return code + ' needs an order of ' + money(from) + '. Add ' + money(from - v) + ' more.';
    return '';
  }
  async removeCoupon(raw, btn) {
    const code = String(raw || '').trim().toUpperCase();
    if (!code) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    btn?.setAttribute('aria-busy', 'true');
    // Off the cookie first: the gift engine copies a cookie-only code back onto
    // the cart whenever it writes, so a pass landing mid-remove re-added it.
    try { const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/); if (m && decodeURIComponent(m[1]).toUpperCase() === code) this.setCodeCookie(''); } catch (e) {}
    try {
      const cart = await (await fetch(root + 'cart.js')).json();
      const keep = (cart.discount_codes || []).map((d) => String(d.code)).filter((c) => c.toUpperCase() !== code);
      const r = await fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: keep.join(','), sections: 'cart-drawer,header' }) });
      const data = await r.json();
      const on = (data.discount_codes || []).filter((d) => d.applicable).map((d) => String(d.code));
      this.setCodeCookie(on[0] || '');
      this.couponMsg(code + ' removed.', true);
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:changed'));
    } catch (err) {
      this.couponMsg("Couldn't remove that code. Please try again.", false);
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  }
  refresh() {
    const at = this._painted || 0;
    return fetch('/?sections=cart-drawer,header')
      .then((r) => r.json())
      .then((s) => {
        // Something repainted the drawer while this was on the wire, usually
        // the gift engine with the cart it just corrected (gift swapped, hamper
        // code applied). This snapshot was taken before that, so painting it
        // would put the old cart back on screen: on a slow phone the drawer sat
        // on "Applying..." and the previous gift while the cart was right.
        if ((this._painted || 0) !== at) return;
        this.renderSections(s);
      })
      .catch(() => {});
  }
  renderSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) this._painted = (this._painted || 0) + 1;
    if (sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const next = parsed.querySelector('[data-cart-body]');
      const foot = parsed.querySelector('[data-cart-foot]');
      const existingBody = this.querySelector('[data-cart-body]');
      const existingFoot = this.querySelector('[data-cart-foot]');
      if (next && existingBody) existingBody.replaceWith(next);
      if (foot && existingFoot) existingFoot.replaceWith(foot);
      this.paintCoupons();
      // Quantities still on their way (quick taps) win over the markup, which
      // was rendered before they landed: any repaint, ours or the gift
      // engine's, would otherwise flash an older number for a moment.
      Object.keys(this._want || {}).forEach((s) => {
        const box = findLine(this, s)?.querySelector('[data-qty-input]');
        if (box) box.value = this._want[s];
      });
    }
    if (sections['header']) {
      const count = new DOMParser().parseFromString(sections['header'], 'text/html').querySelector('[data-cart-count]');
      if (count) document.querySelector('[data-cart-count]')?.replaceWith(count);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);
window.refreshCartSections = (s) => document.querySelector('cart-drawer')?.renderSections(s);

// The /cart page renders the same cart-line-items markup as the drawer, but the
// handlers above are bound to the <cart-drawer> element and never see a click on
// the page, so quantity, remove and bouquet-remove did nothing there at all.
// Delegated on document, scoped to [data-cart-page], and re-rendering the `main`
// section in place so the page stays ajax like the drawer.
//
// The page section is NOT called "main". "main" is only its key in the JSON
// template; the Section Rendering API wants the real id (template--<n>__main),
// so asking for "main" always came back null, the page never repainted, and the
// next "-" worked from the stale number in the box (1 -> "+" -> still showing 1
// -> "-" sent 0 and deleted the line). The id is read off the wrapper now, and
// each step starts from the quantity the cart last reported, not the box.
(function () {
  const cartRoot = () => (window.Shopify?.routes?.root) || '/';

  function pageSectionId() {
    const wrap = document.querySelector('[data-cart-page]')?.closest('.shopify-section');
    return wrap && wrap.id ? wrap.id.replace(/^shopify-section-/, '') : '';
  }

  // Swap in the fresh page markup but keep the LIVE gift-details fields and the
  // discount box: assets/global.js binds its listeners to those exact nodes (and
  // a half-typed gift message lives in them), so replacing them would leave a
  // dead Apply button and unsaved fields.
  function repaintCartPage(html) {
    const cur = document.querySelector('[data-cart-page]');
    const parsed = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-page]');
    if (!cur || !parsed) return false;
    const next = document.adoptNode(parsed);
    ['[data-gift-fields]', '.cartpg__discount'].forEach((sel) => {
      const live = cur.querySelector(sel);
      const fresh = next.querySelector(sel);
      if (live && fresh) fresh.replaceWith(live);
    });
    cur.replaceWith(next);
    return true;
  }
  window.taabaaCartPageSectionId = pageSectionId;
  window.taabaaRepaintCartPage = repaintCartPage;

  function paint(sections, sid) {
    if (!sections) return false;
    const painted = !!(sid && sections[sid] && repaintCartPage(sections[sid]));
    window.refreshCartSections?.(sections);
    return painted;
  }

  // Quantity the cart last confirmed (or is being asked for) per LINE (see
  // lineSig at the top of this file: keys change when a discount comes or
  // goes). Only held while requests are in flight; cleared once the page has
  // settled and been repainted from the server.
  const known = {};
  let inflight = 0;
  let chain = Promise.resolve();
  const page = () => document.querySelector('[data-cart-page]');
  const itemSig = (x) => lineSig(x.variant_id, x.properties);

  function qtyFor(li) {
    const k = liSig(li);
    if (Object.prototype.hasOwnProperty.call(known, k)) return known[k];
    const v = parseInt(li.querySelector('[data-qty-input]')?.value, 10);
    return Number.isNaN(v) ? null : v;
  }

  // wanted: { lineSig: quantity }. Keys are looked up at send time.
  function keyed(wanted) {
    const updates = {};
    Object.keys(wanted).forEach((s) => { const li = findLine(page(), s); if (li) updates[li.dataset.key] = wanted[s]; });
    return updates;
  }
  async function post(updates, sid) {
    try {
      const r = await fetch(`${cartRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: [sid, 'cart-drawer', 'header'].filter(Boolean).join(',') })
      });
      let data = null;
      try { data = await r.json(); } catch (e) { data = null; }
      return { ok: r.ok && !!data && !data.status, status: r.status, data };
    } catch (e) { return { ok: false, status: 0, data: null }; }
  }
  // Did the cart end up holding what was asked for? A stale key can be refused
  // (400) or quietly ignored, so both are checked.
  const landed = (wanted, items) => Object.keys(wanted).every((s) => {
    const it = items.find((x) => itemSig(x) === s);
    return (it ? it.quantity : 0) === wanted[s];
  });

  async function send(wanted) {
    const sid = pageSectionId();
    let res = await post(keyed(wanted), sid);
    if (!res.ok || !landed(wanted, res.data.items || [])) {
      // Re-keyed under us: let the cart settle, repaint, look the lines up
      // again and try once more.
      if (window.taabaaGiftsReady) { try { await window.taabaaGiftsReady(4000); } catch (e) {} }
      try {
        const fresh = await (await fetch(`${cartRoot()}?sections=${[sid, 'cart-drawer', 'header'].filter(Boolean).join(',')}`)).json();
        paint(fresh, sid);
      } catch (e) {}
      res = await post(keyed(wanted), sid);
    }
    const data = res.data;
    if (!res.ok) {
      // refused: forget the optimistic numbers so the next click reads the box
      Object.keys(wanted).forEach((s) => { delete known[s]; });
      return;
    }
    const items = data.items || [];
    Object.keys(wanted).forEach((s) => {
      const it = items.find((x) => itemSig(x) === s);
      known[s] = it ? it.quantity : 0;
    });
    if (!paint(data.sections, sid)) {
      // No fresh markup came back: at least make the boxes tell the truth.
      document.querySelectorAll('[data-cart-page] .cart-item').forEach((li) => {
        const it = items.find((x) => itemSig(x) === liSig(li));
        const box = li.querySelector('[data-qty-input]');
        if (box) box.value = it ? it.quantity : 0;
      });
    }
    document.dispatchEvent(new CustomEvent('cart:changed'));
  }

  function apply(updates) {
    if (!Object.keys(updates).length) return chain;
    Object.keys(updates).forEach((k) => { known[k] = updates[k]; });
    inflight++;
    chain = chain.then(() => send(updates)).catch(() => {}).then(() => {
      inflight--;
      if (!inflight) Object.keys(known).forEach((k) => { delete known[k]; });
    });
    return chain;
  }

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-cart-page]')) return;

    const bouquet = e.target.closest('[data-bouquet-remove]');
    if (bouquet) {
      e.preventDefault();
      const bid = bouquet.getAttribute('data-bouquet-remove');
      const updates = {};
      document.querySelectorAll('[data-cart-page] .cart-item').forEach((li) => {
        if (li.dataset.bouquet === bid) updates[liSig(li)] = 0;
      });
      return apply(updates);
    }

    const remove = e.target.closest('[data-cart-remove]');
    const step = e.target.closest('[data-qty-up],[data-qty-down]');
    if (!remove && !step) return;
    const li = (remove || step).closest('.cart-item');
    if (!li) return;
    e.preventDefault();

    let q = 0;
    if (step) {
      const cur = qtyFor(li);
      if (cur === null) return;
      q = Math.max(0, cur + (step.hasAttribute('data-qty-up') ? 1 : -1));
    }
    apply({ [liSig(li)]: q });
  });

  // typing a quantity straight into the box
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input || !input.closest('[data-cart-page]')) return;
    const li = input.closest('.cart-item');
    if (!li) return;
    const q = Math.max(0, parseInt(input.value, 10));
    if (Number.isNaN(q)) return;
    apply({ [liSig(li)]: q });
  });
})();
